import uuid from '../../lib/uuid';
import { normalizeError } from '../utils';
class Analytics {
    _options = {};
    _waitForBufferingCheck = null;
    listeners = null;
    detachListenerCallback = null;
    timeReportInterval = 10000;
    player;
    lastReportAt = 0;
    lastBufferStart = 0;
    isAttaching = false;
    isPlaying = false;
    isBuffering = false;
    duration = 0;
    durationPlaying = 0;
    activeBufferingDuration = 0;
    totalDurationBuffering = 0;
    maximumPosition = 0;
    viewId = '';
    attachedAt = 0;
    viewerId = '';
    isSetup = false;
    stoppedHACK = false;
    constructor(options) {
        this.updateOptions(options);
    }
    updateOptions(options = {}) {
        this._options = { ...options };
        this.timeReportInterval = this._options.timeInterval !== undefined ? this._options.timeInterval : 10000;
    }
    attach({ video, viewerId }) {
        if (this.listeners) {
            this.detach();
        }
        this.player = video;
        this.lastReportAt = 0;
        this.lastBufferStart = 0;
        this.isAttaching = false;
        this.isPlaying = false;
        this.isBuffering = false;
        this.duration = 0;
        this.durationPlaying = 0;
        this.activeBufferingDuration = 0;
        this.totalDurationBuffering = 0;
        this.maximumPosition = 0;
        this.viewerId = viewerId;
        this.viewId = uuid();
        this.attachedAt = Date.now();
        this.isSetup = false;
        this.listeners = this._createListeners();
        this.detachListenerCallback = video.attachEvents(this.listeners) || null;
    }
    detach() {
        const isAttached = !!this.listeners;
        this.detachListenerCallback?.();
        this.detachListenerCallback = null;
        this.listeners = null;
        this._waitForBufferingCheck && clearTimeout(this._waitForBufferingCheck);
        if (isAttached) {
            this._report('detach', {
                position: this._getCurrentTime(),
                videoDuration: this._getVideoDuration(),
            });
        }
        this.player = undefined;
        return this;
    }
    _createListeners() {
        const listeners = {
            ended: () => {
                this._handleNormalOperation();
                this._report('complete');
                this._handleBufferingEnd();
            },
            error: (error) => {
                this._handlePlaybackError(error);
            },
            pause: () => {
                this._handleNormalOperation();
                this._report('pause');
                this.isPlaying = false;
                this._handleBufferingEnd();
            },
            play: () => {
                this._handleNormalOperation();
                this._report('play');
                this.isPlaying = true;
                this._handleBufferingEnd();
            },
            playing: () => {
                this._handleNormalOperation();
                this.isPlaying = true;
                this.isAttaching = true;
                this._handleBufferingEnd();
            },
            resize: () => {
                this._handleNormalOperation();
                this._report('quality');
                this._handleBufferingEnd();
            },
            seeking: () => {
                this._handleNormalOperation();
                this._report('seek');
            },
            seeked: () => {
                this._handleNormalOperation();
                this._handleBufferingEnd();
            },
            timeupdate: () => {
                this._reportTime();
            },
            stalled: () => {
                this._handleBufferingStart();
            },
            waiting: () => {
                this.isPlaying = false;
                this._handleBufferingStart();
            },
            visibilitychange: () => {
                this._report('visibilitychange');
            },
            pagehide: () => {
                this._report('pagehide');
            },
        };
        return listeners;
    }
    _isActuallyPlaying() {
        return this.player?.isPlaying() || false;
    }
    _getCurrentTime() {
        return this.player?.getCurrentTime() || 0;
    }
    _getVideoDuration() {
        return this.player?.getVideoDuration() || 0;
    }
    _handleBufferingStart() {
        this.isBuffering = true;
        this.lastBufferStart = this.lastBufferStart || Date.now();
        if (this._waitForBufferingCheck) {
            return;
        }
        this._waitForBufferingCheck = setTimeout(() => {
            this._waitForBufferingCheck = null;
            if (!this.isBuffering) {
                return;
            }
            if (this._isActuallyPlaying()) {
                this._handleBufferingEnd();
                return;
            }
            this._report('buffer');
        }, 500);
    }
    _handleNormalOperation() {
        this.stoppedHACK = false;
    }
    _handleBufferingEnd() {
        this.isBuffering = false;
        this.lastBufferStart = 0;
        this._waitForBufferingCheck && clearTimeout(this._waitForBufferingCheck);
        this._waitForBufferingCheck = null;
        this.totalDurationBuffering += this.activeBufferingDuration;
        this.activeBufferingDuration = 0;
    }
    _handlePlaybackError(error = null) {
        if (this.stoppedHACK) {
            console.warn('An error occurred, but playback is stopped so this should not be a problem', error);
        }
        else if (error === null) {
            console.warn('An error occurred, but the error was null');
        }
        else {
            this._report('error', { error: normalizeError(error) });
        }
    }
    _reportTime() {
        if (!this.isSetup || !this.isAttaching) {
            return;
        }
        const n = Date.now();
        if (this.lastReportAt && n - this.lastReportAt <= this.timeReportInterval) {
            return;
        }
        this._report('time');
    }
    _report(action, params) {
        if (!this.isSetup) {
            this.isSetup = true;
            this._report('setup');
        }
        const n = Date.now();
        const durationDelta = this.isAttaching ? n - (this.lastReportAt || n) : 0;
        const durationPlayingDelta = this.isPlaying ? n - (this.lastReportAt || n) : 0;
        const durationBufferingDelta = this.isBuffering ? n - (this.lastBufferStart || n) : 0;
        this.duration += durationDelta;
        this.durationPlaying += durationPlayingDelta;
        this.activeBufferingDuration += durationBufferingDelta;
        this.isAttaching = true;
        this.lastReportAt = n;
        const position = params?.position ?? this._getCurrentTime();
        this.maximumPosition = Math.max(this.maximumPosition, position);
        const metrics = {
            ...params,
            action,
            viewerId: this.viewerId,
            viewId: this.viewId,
            videoId: this._options.videoId,
            userData: this._options.userData ? JSON.parse(JSON.stringify(this._options.userData)) : undefined,
            timestamp: n,
            attachedAt: this.attachedAt,
            position,
            maximumPosition: this.maximumPosition,
            videoDuration: params?.videoDuration ?? this._getVideoDuration(),
            durationDelta: Math.round(durationDelta / 1000),
            duration: Math.round(this.duration / 1000),
            durationPlayingDelta: Math.round(durationPlayingDelta / 1000),
            durationPlaying: Math.round(this.durationPlaying / 1000),
            durationBufferingDelta: Math.round(durationBufferingDelta / 1000),
            durationBuffering: Math.round((this.totalDurationBuffering + this.activeBufferingDuration) / 1000),
        };
        this._options.onReport?.(metrics);
    }
}
export default Analytics;
